/* eslint-disable no-console */
import axios from 'axios';
import get from 'lodash/get';
import { executeAuthRequest } from '@sureifylabs/ext-frontend-identity';
import { checkAuthTypeAndGetAccessToken, isFeatureEnabled } from '../utils';
import host from './host';
import LoggerApi from '../LoggerApi';

const { REACT_APP_BASE_URL } = host();
const { REACT_APP_LIFETIME_URL: LIFETIME_URL } = process.env;
const FILE_PATH = 'src/util/externalServices.js';

const API_ENDPOINTS = {
  ZIP: `${LIFETIME_URL}/api/v1/vesta/usps/city-state-lookup`,
  ADDRESS_VALIDATION: `${LIFETIME_URL}/api/v1/vesta/usps/address-validation`,
  CARDPOINTE_PAYMENT: `${REACT_APP_BASE_URL}/cardpointe-payment`,
  REPLICA: `${REACT_APP_BASE_URL}/replica`,
};

/** ****************** USPS ZIPCODE LOOKUP ******************* */

export const getCityStateLookup = async (zip) => {
  // Check if USPS is enabled
  if (!isFeatureEnabled('REACT_APP_ENABLE_USPS')) {
    return null;
  }

  try {
    const accessToken = await checkAuthTypeAndGetAccessToken();
    const { data } = await axios.get(`${API_ENDPOINTS.ZIP}`, {
      params: { zip },
      headers: {
        access_token: `Bearer ${accessToken}`,
      },
    });
    if (data && data.data) {
      console.log('Received USPS Zip data');
      LoggerApi.info({
        module: 'USPS',
        message: 'Received USPS Zip data',
        data,
        path: FILE_PATH,
        funcName: 'getCityStateLookup',
        line: 22,
      });
      return data.data;
    }
    console.log('USPS Zip data empty');
    LoggerApi.info({
      module: 'USPS',
      message: 'Received USPS Zip data empty',
      data,
      path: FILE_PATH,
      funcName: 'getCityStateLookup',
      line: 22,
    });
    return null;
  } catch (error) {
    console.log({ ...error });
    const errorResponse = error?.response?.data || { message: error.message, status: error.status };
    LoggerApi.error({
      module: 'USPS',
      message: 'Error in executing USPS city-state-lookup api',
      data: errorResponse,
      path: FILE_PATH,
      funcName: 'getCityStateLookup',
      line: 22,
    });
    // to not break the flow if network call fails
    return null;
  }
};

/** ****************** USPS ADDRESS VALIDATION ******************* */

// service method to get valid address from provided address
export const getAddressValidation = async ({ zip, addressLine, city, state }) => {
  // Check if USPS is enabled
  if (!isFeatureEnabled('REACT_APP_ENABLE_USPS')) {
    return null;
  }

  try {
    const accessToken = await checkAuthTypeAndGetAccessToken();
    const { data } = await axios.get(`${API_ENDPOINTS.ADDRESS_VALIDATION}`, {
      params: { zip, address_line: addressLine, city, state },
      headers: {
        access_token: `Bearer ${accessToken}`,
      },
    });
    if (data && data.data) {
      console.log('Received USPS Address Validation data');
      return data.data;
    }
    console.log('USPS Address Validation data empty');
    return null;
  } catch (e) {
    console.log(e);
    return null;
  }
};

/** ****************** CARDPOINTE PAYMENT ******************* */

export const postMakeCardpointePayment = async ({ payload }) => {
  try {
    const accessToken = await checkAuthTypeAndGetAccessToken();
    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };
    const { data, status } = await axios.post(`${API_ENDPOINTS.CARDPOINTE_PAYMENT}`, payload, {
      headers,
    });
    if (status && data && data.response) {
      return { errors: null, data: data.response };
    }
    return { errors: 'Some Error Occurred', data: null };
  } catch (e) {
    return { errors: 'API FAILED', data: null };
  }
};

/** ****************** SSO AUTH ******************* */

/**
 * @description Handles IDP/SP Initiated SSO login.
 * @param {Object} ssoAuthConfig
 */
export const executeSSOAuthentication = async (ssoAuthConfig) => {
  const { SSO_USER_TYPE, SSO_REDIRECT_URL, IS_SSO_IDP_INITIATED_FLOW } = ssoAuthConfig;

  try {
    await executeAuthRequest({
      baseUri: LIFETIME_URL,
      userType: SSO_USER_TYPE,
      redirectUrl: SSO_REDIRECT_URL,
      isIdPInitiatedFlow: IS_SSO_IDP_INITIATED_FLOW,
    });
  } catch (e) {
    console.error('There was an error while calling executeAuthRequest function');
    console.error(e);
  }
};

/** ****************** COPY APP ******************* */

export const postMakeCopyApp = async (partyId) => {
  try {
    const accessToken = await checkAuthTypeAndGetAccessToken();
    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };
    const response = await axios.post(
      `${API_ENDPOINTS.REPLICA}`,
      { party_id: partyId },
      { headers },
    );
    const replicaAppUrl = get(response, 'data.response.application_url');
    if (replicaAppUrl) {
      return replicaAppUrl;
    }
    console.log('DEBUG', response);
    return null;
  } catch (e) {
    console.log('API FAILED', e);
    return 'API FAILED';
  }
};
